<template>
  <div
    ref="form"
    tag="form"
    @submit.prevent="onSubmit"
  >
    <OreInputText
      v-if="showComment"
      :checker="{ params: { required: true } }"
      :label="$t(labelComment)"
      :aria-label="$t(labelComment)"
      :value="comment"
      :vid="comment"
      :max-length="maxLength"
      :helper-message="commentHelperMessage"
      :helper-type="commentHelperType"
      @update:value="updateComment($event)"
    />
    <b-collapse
      v-else-if="Object.keys(format).length !== 0"
      v-model="isOpen"
      animation="slide"
      class="cardForm collapseCard"
      tabindex="0"
      @keyup.enter="isOpen = !isOpen"
    >
      <template #trigger="detail">
        <div
          :aria-expanded="isOpen"
          aria-controls="contentIdForA11y2"
          class="card-header solid"
        >
          <p
            class="card-header-title"
            :aria-label="description"
          >
            {{ description }}
          </p>
          <a class="card-header-icon">
            <b-icon
              :icon="detail.open ? 'chevron-up' : 'chevron-down'"
              type="is-primary"
            />
          </a>
        </div>
      </template>
      <div
        v-for="(item, key) in format"
        :key="key"
      >
        <OreInputNumber
          v-if="item.checker.type === 'IntegerChecker' || item.checker.type === 'FloatChecker'"
          :checker="item.checker"
          :label="
            internationalisationService.getLocaleforPath(application, pathForKey + '.' + key, key)
          "
          :aria-label="
            internationalisationService.getLocaleforPath(application, pathForKey + '.' + key, key)
          "
          :value="
            fields[key]
              ? item.checker.type === 'FloatChecker'
                ? parseFloat(fields[key])
                : parseInt(fields[key])
              : 0
          "
          :vid="key"
          @update:value="updateValue(key, $event)"
        />
        <OreInputDate
          v-else-if="item.checker.type === 'DateChecker'"
          :checker="item.checker"
          :label="
            internationalisationService.getLocaleforPath(application, pathForKey + '.' + key, key)
          "
          :aria-label="
            internationalisationService.getLocaleforPath(application, pathForKey + '.' + key, key)
          "
          :value="fields[key] ? fields[key] : new Date(null)"
          :vid="key"
          @update:value="updateValue(key, $event)"
        />
        <OreInputReference
          v-else-if="item.checker.type === 'ReferenceChecker' && item.checker.refType"
          :checker="item.checker"
          :label="
            internationalisationService.getLocaleforPath(application, pathForKey + '.' + key, key)
          "
          :aria-label="
            internationalisationService.getLocaleforPath(application, pathForKey + '.' + key, key)
          "
          :references="referencesScopes[key] ? referencesScopes[key].rows : []"
          :value="fields[key]"
          :vid="key"
          @update:value="updateValue(key, $event)"
        />
        <OreInputText
          v-else
          :checker="item.checker"
          :label="
            internationalisationService.getLocaleforPath(application, pathForKey + '.' + key, key)
          "
          :aria-label="
            internationalisationService.getLocaleforPath(application, pathForKey + '.' + key, key)
          "
          :value="fields[key]"
          :vid="key"
          @update:value="updateValue(key, $event)"
        />
      </div>
    </b-collapse>
  </div>
</template>

<script lang="ts">
import OreInputText from "@/components/common/provider/OreInputText.vue";
import OreInputNumber from "@/components/common/provider/OreInputNumber.vue";
import OreInputDate from "@/components/common/provider/OreInputDate.vue";
import OreInputReference from "@/components/common/provider/OreInputReference.vue";
import { InternationalisationService } from "@/services/InternationalisationService";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import services from "@/composable/services";
import { BCollapse, BIcon } from "buefy";
import "@/style/components/FieldsForm.scss";
import { validateRequestComment } from "@/composable/validation/commentValidator";

export default {
  name: "FieldsForm",
  components: {
    BIcon,
    BCollapse,
    OreInputDate,
    OreInputReference,
    OreInputNumber,
    OreInputText,
  },
  props: {
    application: Object,
    pathForKey: String,
    format: Object,
    description: String,
    fields: Object,
    comment: {
      type: String,
      default: "",
    },
    showComment: Boolean,
    maxLength: {
      type: Number,
      required: false,
      default: 200,
    },
    labelComment: {
      type: String,
      required: false,
      default: "applications.comment",
    },
    /**
     * Longueur minimale exigée pour le commentaire ( après trim ) .
     * Active la validation côté UI ( bordure rouge / verte + message
     * d'aide + erreur i18n émise au parent ) . 0 = pas de minimum
     * appliqué , garde le comportement historique permissif .
     */
    commentMinLength: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  emits: ["update:fields", "update:comment"],
  setup(props, ctx) {
    const { t } = useI18n();
    const fields = ref(props.fields);
    const referencesScopes = ref({});
    const formRef = ref(null);
    watch(
      () => props.fields,
      (newValue) => {
        if (newValue) {
          fields.value = ref(newValue);
        }
      },
      { immediate: true },
    );
    onMounted(async () => {
      const newReferencesScopes: any = {};
      for (let item in props.format) {
        if (
          props.format[item].checker
          && props.format[item].checker.type === "ReferenceChecker"
          && props.format[item].checker.refType
        ) {
          newReferencesScopes[item] = await services.dataService.getData(
            props.application.name,
            props.format[item].checker.refType,
          );
        }
      }
      referencesScopes.value = newReferencesScopes;
    });

    function onSubmit(event: any) {
      console.log("submit", event);
    }
    function updateValue(key: string, event: any) {
      fields.value = event;
      let localeFields = fields.value;
      ctx.emit("update:fields", { fields: localeFields, key });
    }

    /**
     * Etat de validation courant du commentaire ( recalculé à chaque
     * frappe ) . Quand {@code commentMinLength === 0} , validation
     * désactivée et l'état reste {valid:true} - back-compat .
     */
    const commentValidation = computed(() => {
      if ((props.commentMinLength ?? 0) <= 0) {
        return { valid: true, errorKey: null, params: {} as Record<string, number> };
      }
      return validateRequestComment(props.comment, props.commentMinLength);
    });

    /** Message i18n résolu à passer en `helperMessage` à OreInputText . */
    const commentHelperMessage = computed<string>(() => {
      const v = commentValidation.value;
      if (v.valid || !v.errorKey) return "";
      return t(v.errorKey, v.params);
    });

    /** Type Buefy à pousser sur le b-field ( bordure / icône ) . */
    const commentHelperType = computed<string>(() => {
      if ((props.commentMinLength ?? 0) <= 0) return "";
      return commentValidation.value.valid ? "is-success" : "is-danger";
    });

    function updateComment(event: string) {
      const result = (props.commentMinLength ?? 0) > 0
        ? validateRequestComment(event, props.commentMinLength)
        : { valid: event !== "", errorKey: null, params: {} as Record<string, number> };
      ctx.emit("update:comment", {
        comment: event,
        valid: result.valid,
        errorKey: result.errorKey,
        errorParams: result.params,
      });
    }
    return {
      updateComment,
      updateValue,
      onSubmit,
      referencesScopes,
      formRef,
      commentHelperMessage,
      commentHelperType,
    };
  },
  data: () => {
    return {
      isOpen: true,
      internationalisationService: InternationalisationService.INSTANCE,
    };
  },
  methods: {},
};
</script>

<style scoped>
.required {
  color: rgb(255, 0, 0);
  padding-right: 5px;
  font-size: 150%;
}
</style>
